/**
 * The playback-state sink the Session Flight Recorder observes `useMediaPlayback` through
 * (AVS-4423 Task 1.7).
 *
 * A forced mute is otherwise invisible. When autoplay policy rejects `play()` with
 * `NotAllowedError`, `hooks/useMediaPlayback.ts` mutes the element to recover (frames still render,
 * audio does not) and waits for the next user gesture to lift it. Nothing recorded that moment, so
 * a recording of that call shows a healthy stream, `framesDecoded` climbing, and a user who heard
 * silence — one of the three blind spots the 2026-09-04 multi-party incident review found.
 *
 * **Zero imports.** Same discipline as `utils/ui/app.ts` (`setListenerGuardReporter` /
 * `setEmitRecorder`, `utils/events.ts:61`/`:105`): a module-level settable function defaulting to a
 * no-op, called from INSIDE the owning code, wrapped in its own try/catch so a throwing sink can
 * never break the playback ladder it is observing. `useMediaPlayback.ts` is a hook shared by both
 * `<Video>` and `<AudioSink>` and reachable on every VOIP tile render, so nothing this module does
 * may add weight or a cycle to that path.
 *
 * **The hook reports; it does not decide.** This module only carries the fact — which kind of
 * element, and which transition. Deciding the recorder LANE and PRIORITY for that fact is
 * `telemetry/recorder/taps/rtcTap.ts`'s job, the same separation `app.ts`'s sinks keep from the taps
 * that register them.
 */

export type TMediaPlaybackKind = 'video' | 'audio';
export type TMediaPlaybackState = 'forced-mute' | 'restored';

export type TMediaPlaybackSink = (kind: TMediaPlaybackKind, state: TMediaPlaybackState) => void;

function noopMediaPlaybackSink(): void {
  // Default sink: no recorder installed, and `reportPlaybackState` pays one no-op call.
}

let mediaPlaybackSink: TMediaPlaybackSink = noopMediaPlaybackSink;

/** Register the sink. Pass `null` to restore the no-op. */
export function setMediaPlaybackSink(sink: TMediaPlaybackSink | null): void {
  mediaPlaybackSink = sink ?? noopMediaPlaybackSink;
}

/**
 * Report one playback-state transition. Called from inside `useMediaPlayback`'s ladder; never
 * throws into its caller, because a throwing sink must not be the reason a forced-mute recovery
 * fails to run.
 */
export function reportPlaybackState(kind: TMediaPlaybackKind, state: TMediaPlaybackState): void {
  try {
    mediaPlaybackSink(kind, state);
  } catch (error) {
    console.error('[mediaPlaybackSink] sink threw and was isolated', error);
  }
}
